/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { BrandingUiInfoKeys } from '../../../core/entities';
import { DefaultServicesClientImpl } from '../../../core/impls';
import { Locale, LocaleKeys } from '../../../core/locale';
import { NetworkManager } from '../../../core/network/NetworkManager';
import type { TivoPayloadAction } from '../../../core/tivo/tivo-redux-wrapper';
import { ListTypes } from '../../../core/utils';
import {
    onKeyDownList,
    onKeyUpList,
    onMouseOutList,
    onMouseOverList,
} from '../../list/list.reducers';
import {
    showResetAppOverlay,
    showSignOutOverlay,
} from '../accountsoverlays/accountsoverlays.reducers';
import { setAccountOptionsListAction, setSelectedAccountOption } from './account.actions';
import { SET_ACCOUNT_OPTIONS_LIST, SET_ACCOUNT_SELECTED_OPTION } from './account.types';
import { IAccountSelectedOption } from './IAccountSelectedOption';

interface IAccount {
    optionsList: Array<IAccountSelectedOption>;
    selectedOption?: IAccountSelectedOption;
}

const initialState: IAccount = {
    optionsList: [],
};

const accountReducer = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<IAccount>>{},
) => {
    switch (type) {
        case SET_ACCOUNT_OPTIONS_LIST:
            return {
                ...state,
                optionsList: payload.optionsList,
                selectedOption: state.selectedOption ?? payload.optionsList[0],
            };
        case SET_ACCOUNT_SELECTED_OPTION:
            return {
                ...state,
                selectedOption: payload.selectedOption,
            };
        default:
            return state;
    }
};

export const setAccountOptionsList = () => (dispatch: Function) => {
    const displayName = new DefaultServicesClientImpl().getBrandingUiInfoForKey(
        BrandingUiInfoKeys.DISPLAY_NAME,
    );
    const accountItemList: Array<IAccountSelectedOption> = [
        {
            title: Locale.getString(LocaleKeys.SETTINGS_SIGN_OUT),
            preview: Locale.getString(LocaleKeys.SETTINGS_ACCOUNT_SIGN_OUT_PREVIEW_TEXT).replace(
                'DISPLAY_NAME',
                displayName,
            ),
            isDisabled: NetworkManager.isNetworkDisconnected(),
        },
        {
            title: Locale.getString(LocaleKeys.SETTINGS_RESET_APP),
            preview: Locale.getString(LocaleKeys.SETTINGS_ACCOUNT_RESET_PREVIEW_TEXT).replace(
                'DISPLAY_NAME',
                displayName,
            ),
            isDisabled: false,
        },
    ];
    dispatch(setAccountOptionsListAction(accountItemList));
};

/**
 * Below reducers are related to operations on key events.
 */

export const onKeyDownAccountOperations = () => (dispatch: Function, getState: Function) => {
    const {
        list: {
            [ListTypes.ACCOUNT]: { position },
        },
        account: { optionsList },
    } = getState();
    if (position < optionsList.length - 1) {
        dispatch(setSelectedAccountOption(optionsList[position + 1]));
        dispatch(onKeyDownList(ListTypes.ACCOUNT));
    }
};

export const onKeyOkAccountOperations = () => (dispatch: Function, getState: Function) => {
    const {
        list: {
            [ListTypes.ACCOUNT]: { position },
        },
        account: { optionsList },
    } = getState();
    const selectedItem = optionsList[position].title;
    switch (selectedItem) {
        case Locale.getString(LocaleKeys.SETTINGS_SIGN_OUT):
            if (!NetworkManager.isNetworkDisconnected()) {
                dispatch(showSignOutOverlay());
            }
            break;

        case Locale.getString(LocaleKeys.SETTINGS_RESET_APP):
            dispatch(showResetAppOverlay());
            break;

        default:
    }
};

export const onKeyUpAccountOperations = () => (dispatch: Function, getState: Function) => {
    const {
        list: {
            [ListTypes.ACCOUNT]: { position },
        },
        account: { optionsList },
    } = getState();
    if (position > 0) {
        dispatch(setSelectedAccountOption(optionsList[position - 1]));
        dispatch(onKeyUpList(ListTypes.ACCOUNT));
    }
};

export const onMouseClickAccountOperations = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;
    if (value.includes(ListTypes.ACCOUNT)) dispatch(onKeyOkAccountOperations());
};

export const onMouseOutAccountOperations = () => (dispatch: Function) => {
    dispatch(onMouseOutList());
};

export const onMouseOverAccountOperations = () => (dispatch: Function, getState: Function) => {
    dispatch(onMouseOverList());
    const {
        list: {
            [ListTypes.ACCOUNT]: { position },
        },
        account: { optionsList },
    } = getState();
    dispatch(setSelectedAccountOption(optionsList[position]));
};

export default accountReducer;
